import type { ElementType } from 'react';
import { BarChart3, Check, FileText } from 'lucide-react';

import { Spinner } from '@/components/glitch-ui/spinner';
import { tabButtonClass } from '@/components/glitch-ui/tab-card-panel';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { ReportType, ReportTypeOption } from '@/lib/types';

/** A report the legacy generators produce, offered while the report engine does not own its type. */
export interface LegacyReportOption {
  key: string;
  label: string;
  icon: ElementType;
  iconColor: string;
  reportTypes: ReportType[];
  disabled: boolean;
  pending: boolean;
  done: boolean;
  onSelect: () => void;
}

export interface ReportMenuItem {
  key: string;
  label: string;
  icon: ElementType;
  iconColor: string;
  disabled: boolean;
  reason: string | null;
  pending: boolean;
  done: boolean;
  onSelect: () => void;
}

/**
 * The "+ Add" entries: every catalogue type the organization may request, each replacing the legacy entry that
 * produces the same report types, then the legacy entries the catalogue does not cover. An unavailable catalogue
 * type stays listed, disabled, with its reason.
 */
export function reportMenuItems(
  catalogue: ReportTypeOption[],
  legacy: LegacyReportOption[],
  onSelectType: (option: ReportTypeOption) => void,
  pendingKey: string | null,
): ReportMenuItem[] {
  const covered = new Set(catalogue.flatMap((option) => option.report_types));
  const engineItems = catalogue.map((option): ReportMenuItem => {
    const replaced = legacy.find((item) => item.reportTypes.some((type) => option.report_types.includes(type)));
    return {
      key: option.key,
      label: option.label,
      icon: replaced?.icon ?? (option.key === 'performance' ? BarChart3 : FileText),
      iconColor: replaced?.iconColor ?? 'text-[#290BD9]',
      disabled: !option.available || pendingKey === option.key,
      reason: option.available ? null : option.reason,
      pending: pendingKey === option.key,
      done: false,
      onSelect: () => onSelectType(option),
    };
  });
  const legacyItems = legacy
    .filter((item) => !item.reportTypes.some((type) => covered.has(type)))
    .map((item): ReportMenuItem => ({ ...item, reason: null }));
  return [...engineItems, ...legacyItems];
}

export function ReportAddMenu({ items }: { items: ReportMenuItem[] }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={tabButtonClass(false)}>+ Add</button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <div className="p-3 space-y-1">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Generate Report</h3>
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                onClick={item.onSelect}
                disabled={item.disabled}
                className="w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-left"
              >
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${item.iconColor}`} />
                    {item.label}
                  </div>
                  {item.reason && <span className="pl-6 text-xs font-normal text-gray-500">{item.reason}</span>}
                </div>
                {item.pending && <Spinner size="sm" />}
                {!item.pending && item.done && <Check className="h-4 w-4 text-green-600" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
